import { useState, useEffect, useCallback } from 'react';
import { Bell, Check, CheckCheck, Trash2, Loader2, AlertCircle, Info, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [allNotifications, setAllNotifications] = useState([]);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/notifications?limit=10`, { withCredentials: true });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  const fetchAllNotifications = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/notifications?limit=100`, { withCredentials: true });
      setAllNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Error fetching all notifications:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/notifications/${notificationId}/read`, {}, { withCredentials: true });
      fetchNotifications();
      if (viewAllOpen) fetchAllNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/notifications/mark-all-read`, {}, { withCredentials: true });
      fetchNotifications();
      if (viewAllOpen) fetchAllNotifications();
      toast.success('Todas as notificações marcadas como lidas');
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Erro ao marcar notificações');
    } finally {
      setLoading(false);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/notifications/${notificationId}`, { withCredentials: true });
      fetchNotifications();
      if (viewAllOpen) fetchAllNotifications();
      toast.success('Notificação eliminada');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Erro ao eliminar notificação');
    }
  };

  const handleViewAll = () => {
    setOpen(false);
    setViewAllOpen(true);
    fetchAllNotifications();
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <AlertCircle className="w-4 h-4 text-red-600" />;
      case 'warning': return <AlertTriangle className="w-4 h-4 text-amber-600" />;
      case 'success': return <CheckCircle className="w-4 h-4 text-green-600" />;
      default: return <Info className="w-4 h-4 text-blue-600" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'warning': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'success': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-blue-100 text-blue-700 border-blue-200';
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      'consent_expiry': 'Expiração',
      'rtbf_request': 'RTBF',
      'breach_alert': 'Violação',
      'system': 'Sistema',
      'marketing': 'Marketing',
      'approval': 'Aprovação',
    };
    return labels[type] || type;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Agora';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} h`;
    return date.toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' });
  };

  const NotificationItem = ({ notification, compact = false }) => (
    <div 
      className={`px-3 py-2 border-b last:border-0 hover:bg-slate-50 transition-colors ${
        !notification.is_read ? 'bg-blue-50/50' : ''
      }`}
    >
      <div className="flex items-start gap-2">
        <div className="mt-1">{getSeverityIcon(notification.severity)}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Badge 
              variant="outline" 
              className={`text-xs ${getSeverityColor(notification.severity)}`}
            >
              {getTypeLabel(notification.type)}
            </Badge>
            <span className="text-xs text-slate-400">
              {formatTime(notification.created_at)}
            </span>
            {!notification.is_read && (
              <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
            )}
          </div>
          <p className="text-sm font-medium text-slate-800 truncate">{notification.title}</p>
          <p className={`text-xs text-slate-500 ${compact ? 'line-clamp-1' : 'line-clamp-2'}`}>
            {notification.message}
          </p>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          {!notification.is_read && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-6 w-6"
              onClick={(e) => {
                e.stopPropagation();
                markAsRead(notification.notification_id);
              }}
              title="Marcar como lida"
            >
              <Check className="w-3 h-3" />
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-6 w-6 text-red-500 hover:text-red-600 hover:bg-red-50"
            onClick={(e) => {
              e.stopPropagation();
              deleteNotification(notification.notification_id);
            }}
            title="Eliminar"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
            <Bell className="w-5 h-5 text-slate-600" />
            {unreadCount > 0 && (
              <Badge 
                className="absolute -top-1 -right-1 h-5 min-w-[20px] flex items-center justify-center bg-red-500 text-white text-xs px-1 animate-pulse"
                data-testid="notification-badge"
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </Badge>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-96" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-3 py-2 border-b bg-slate-50">
            <h4 className="font-semibold text-sm">Notificações</h4>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {unreadCount} não lidas
                </Badge>
              )}
              {unreadCount > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-xs h-7"
                  onClick={markAllAsRead}
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <>
                      <CheckCheck className="w-3 h-3 mr-1" />
                      Marcar todas
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
          
          <ScrollArea className="max-h-96">
            {notifications.length === 0 ? (
              <div className="py-8 text-center text-slate-400">
                <Bell className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm font-medium">Sem notificações</p>
                <p className="text-xs mt-1">As suas notificações aparecerão aqui</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <NotificationItem 
                  key={notification.notification_id} 
                  notification={notification}
                  compact
                />
              ))
            )}
          </ScrollArea>
          
          {notifications.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <div className="p-2 text-center">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-xs w-full text-blue-600 hover:text-blue-700"
                  onClick={handleViewAll}
                >
                  Ver todas as notificações
                </Button>
              </div>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Full Notifications Dialog */}
      <Dialog open={viewAllOpen} onOpenChange={setViewAllOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Todas as Notificações
            </DialogTitle>
            <DialogDescription>
              {allNotifications.length} notificações | {allNotifications.filter(n => !n.is_read).length} não lidas
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex justify-end mb-2">
            {allNotifications.some(n => !n.is_read) && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={markAllAsRead}
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CheckCheck className="w-4 h-4 mr-2" />
                )}
                Marcar todas como lidas
              </Button>
            )}
          </div>
          
          <ScrollArea className="h-[50vh] border rounded-lg">
            {allNotifications.length === 0 ? (
              <div className="py-12 text-center text-slate-400">
                <Bell className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">Sem notificações</p>
              </div>
            ) : (
              allNotifications.map((notification) => (
                <NotificationItem 
                  key={notification.notification_id} 
                  notification={notification}
                />
              ))
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotificationBell;
